import PropTypes from 'prop-types';

import { i18nMoment } from 'wanderu-i18n';

import {
    CREDIT_CARD_SECTION,
    CREDIT_CARD_SECTION_FIELDS,
    PATH_DELIMITER,
    EXT_SEARCH_DIRECTION_DELIM,
    EXT_SEARCH_TRIP_DELIM,
    EXT_SEARCH_DELIM,
    BROWSER_LIST,
    LIVE_CHECK_TRIP_NOT_FOUND_ERRORS,
} from '../constants';

function fieldsPropsShape() {
    return PropTypes.shape({
        fields: PropTypes.arrayOf(fieldsPropsShape),
        helptext: PropTypes.string,
        id: PropTypes.string,
        label: PropTypes.string,
        placeholder: PropTypes.string,
        sensitive: PropTypes.bool,
        type: PropTypes.string,
        validation: PropTypes.shape({
            isRequired: PropTypes.bool,
            maxLength: PropTypes.number,
            serverSide: PropTypes.bool,
        }),
    }).apply(this, arguments);
}

function getFormField(form, path, defaultValue) {
    return path
        .split(PATH_DELIMITER)
        .reduce((currentForm, key) => currentForm?.fields.find(({ id }) => id === key), form)
        || defaultValue;
}

function updateFormHelper(form, keys, updates) {
    if (keys.length === 0) {
        return {
            ...form,
            isModified: true,
            ...updates,
        };
    }

    const key = keys.shift();

    return {
        ...form,
        fields: form.fields.map((field) => (
            field.id === key
                ? updateFormHelper(field, keys, updates)
                : field
        )),
    };
}

function updateForm(form, path, updates) {
    const keys = path.split(PATH_DELIMITER);

    if (!form) {
        return undefined;
    }

    return updateFormHelper(form, keys, updates);
}

function updateSelectOptionsByTypeHelper(form, keys, updates) {
    if (keys.length === 1) {
        return {
            ...form,
            fields: form.fields.map((field) => (
                field.type === updates.fieldType
                    ? {
                        ...field,
                        value: updates.values.find(
                            (fieldValue) => fieldValue.id === field.value,
                        )?.id,
                        values: updates.values,
                    }
                    : field
            )),
        };
    }

    const key = keys.shift();
    return {
        ...form,
        fields: form.fields.map((field) => (
            field.id === key
                ? updateSelectOptionsByTypeHelper(field, keys, updates)
                : field
        )),
    };
}

function updateSelectOptionsByType(form, path, updates) {
    const keys = path.split(PATH_DELIMITER);

    return updateSelectOptionsByTypeHelper(form, keys, updates);
}

function extractItinerary(itineraryId) {
    const [departTripId, returnTripId] = itineraryId.split(EXT_SEARCH_DIRECTION_DELIM);
    const departLegIds = departTripId.split(EXT_SEARCH_TRIP_DELIM);
    const returnLegIds = returnTripId
        ? returnTripId.split(EXT_SEARCH_TRIP_DELIM)
        : [];

    let tripSummaryUrl = `/tripsummary/${departTripId}/`;

    if (returnTripId) {
        tripSummaryUrl += `${returnTripId}/`;
    }

    return {
        departLegIds,
        itineraryId,
        returnLegIds,
        tripIds: [...departLegIds, ...returnLegIds],
        tripSummaryUrl,
    };
}

function extractTokenMap(tripIds, tokenString) {
    const tokens = tokenString.split(EXT_SEARCH_DELIM);

    return tripIds.reduce((obj, tripId, idx) => ({
        ...obj,
        [tripId]: tokens[idx],
    }), {});
}

function extractSelectedFaresMap(tripIds, selectedFaresString) {
    if (!selectedFaresString) {
        return undefined;
    }

    const selectedFares = selectedFaresString
        .split(EXT_SEARCH_DIRECTION_DELIM);

    return tripIds.reduce((obj, tripId, index) => {
        if (selectedFares[index]) {
            return {
                ...obj,
                [tripId]: selectedFares[index],
            };
        }

        return obj;
    }, {});
}

function statusContainsError(status) {
    const {
        isRecoverableError,
        isRetrySearchError,
        isRetryableError,
        isUnknownError,
    } = status;

    return [
        isRecoverableError,
        isRetrySearchError,
        isRetryableError,
        isUnknownError,
    ].some(Boolean);
}

function tripHasError(trip) {
    return !!trip.bookingStatus && statusContainsError(trip.bookingStatus);
}

function tripsContainError(trips) {
    return trips.some((trip) => tripHasError(trip));
}

function getFirstInvalidField(invalidFields) {
    let firstInvalidElement = invalidFields[0];

    if (CREDIT_CARD_SECTION_FIELDS.includes(firstInvalidElement)) {
        firstInvalidElement = CREDIT_CARD_SECTION;
    }

    return firstInvalidElement;
}

function getDisplayPriceForTripLeg(pricingObj) {
    const currency = Object.keys(pricingObj)[0];

    return {
        [currency]: pricingObj[currency].price,
    };
}

function getPriceListForTripLeg({ fees, pricing }) {
    return fees.reduce((priceArray, fee) => ([
        ...priceArray,
        fee.pricing,
    ]), [getDisplayPriceForTripLeg(pricing)]);
}

function getPriceListForTripDirection(tripDirection) {
    return tripDirection.legs.reduce((priceList, leg) => ([
        ...priceList,
        ...getPriceListForTripLeg({
            fees: leg.fees,
            pricing: leg.pricing,
        }),
    ]), []);
}

function getPriceListForTrip({ departTrip, returnTrip }) {
    let tripPriceList = getPriceListForTripDirection(departTrip);
    if (returnTrip) {
        tripPriceList = [
            ...tripPriceList,
            ...getPriceListForTripDirection(returnTrip),
        ];
    }

    return tripPriceList;
}

function getPriceListForTripDirectionForEvents(tripDirection) {
    return tripDirection.legs.reduce((priceList, leg) => ([
        ...priceList,
        {
            [leg.trip_id]: {
                fees: leg.fees,
                pricing: leg.pricing,
            },
        },
    ]), []);
}

function getPriceListForTripForEvents({ departTrip, returnTrip }) {
    let tripPriceList = getPriceListForTripDirectionForEvents(departTrip);
    if (returnTrip) {
        tripPriceList = [
            ...tripPriceList,
            ...getPriceListForTripDirectionForEvents(returnTrip),
        ];
    }

    return tripPriceList;
}

function findBrowser() {
    const { userAgent } = navigator;
    const browser = BROWSER_LIST.find(({ subString }) => userAgent.includes(subString));

    if (!browser) {
        return {};
    }

    return {
        browser: browser.identity,
        substring: browser.subString,
    };
}

function searchVersion(dataString, substring) {
    const index = dataString.indexOf(substring);
    if (index === -1) {
        return undefined;
    }
    const rv = dataString.indexOf('rv:');

    if (substring === 'Trident' && rv !== -1) {
        return parseFloat(dataString.substring(rv + 3));
    }
    return parseFloat(dataString.substring(index + substring.length + 1));
}

function browserDetect() {
    const { browser = 'Other', substring } = findBrowser();
    const browserVersion = searchVersion(navigator.userAgent, substring) || searchVersion(navigator.appVersion, substring) || 'Unknown';

    return {
        browser,
        browserVersion,
        mobile: window.matchMedia('(max-width: 980px)').matches,
    };
}

function tripBookingCompleted(trip) {
    return trip?.bookingStatus?.status === 'COMPLETED';
}

function checkTripNotFound(errorMap) {
    return LIVE_CHECK_TRIP_NOT_FOUND_ERRORS.includes(errorMap?.code);
}

function setAllFormFieldsAsModified(form) {
    const fields = form.fields.map((field) => {
        let modifiedField = { ...field };
        if (!!field.fields && field.fields.length) {
            modifiedField = setAllFormFieldsAsModified(field);
        }

        return {
            ...modifiedField,
            isModified: true,
        };
    });

    return {
        ...form,
        fields,
    };
}

function identifyFields(fields) {
    const ids = [];
    function traverseAndLogIds(obj) {
        if (Array.isArray(obj)) {
            obj.forEach(item => traverseAndLogIds(item));
        } else if (typeof obj === 'object' && obj !== null) {
            if (obj.id) {
                ids.push(obj.id);
            }
            if (obj.fields) {
                traverseAndLogIds(obj.fields);
            }
        }
    }
    traverseAndLogIds(fields);
    return ids;
}

const formatTripForEvents = ({
    direction,
    duration,
    trip,
    isRouted,
}) => {
    const {
        carrier,
        depart_datetime: departTime,
        depart_id: departId,
        arrive_datetime: arriveTime,
        arrive_id: arriveId,
        vehicle_types: vehicleTypes,
        transfers,
    } = trip;
    const mode = vehicleTypes?.every(type => type === vehicleTypes[0])
        ? trip.vehicle_types[0]
        : 'mixed';

    return {
        arrival_station_code: arriveId,
        arrival_time: arriveTime,
        carrier_code: carrier,
        departure_station_code: departId,
        departure_time: departTime,
        direction,
        duration_in_minutes: duration,
        is_routed: isRouted,
        mode,
        transfer_count: transfers,
    };
};

const formatBookingForEvents = ({
    bookingSessionId,
    departTrips,
    itineraryId,
    orderId,
    returnTrips,
    seats,
}) => {
    const trips = departTrips?.map(trip => {
        return formatTripForEvents({
            direction: 'outbound',
            duration: Math.round(i18nMoment.duration(trip.duration, 'hours').asMinutes()),
            isRouted: departTrips?.length > 1,
            trip,
        });
    });

    if (returnTrips.length) {
        returnTrips?.map(trip => {
            return trips.push(formatTripForEvents({
                direction: 'return',
                duration: Math.round(i18nMoment.duration(trip.duration, 'hours').asMinutes()),
                isRouted: returnTrips?.length > 1,
                trip,
            }));
        });
    }

    return {
        booking_session_id: bookingSessionId,
        is_one_way: !returnTrips.length,
        itinerary_id: itineraryId,
        order_id: orderId,
        pax_count: seats,
        trips,
    };
};

export {
    browserDetect,
    checkTripNotFound,
    extractItinerary,
    extractSelectedFaresMap,
    extractTokenMap,
    fieldsPropsShape,
    formatBookingForEvents,
    getDisplayPriceForTripLeg,
    getFirstInvalidField,
    getFormField,
    getPriceListForTrip,
    getPriceListForTripForEvents,
    getPriceListForTripLeg,
    identifyFields,
    setAllFormFieldsAsModified,
    statusContainsError,
    tripBookingCompleted,
    tripHasError,
    tripsContainError,
    updateForm,
    updateSelectOptionsByType,
};
